import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import JInspectorTitleBar from './JInspectorTitleBar';
import JInspectorFields from './JInspectorFields';
import JInspectorReadonly from './JInspectorReadonly';
import JInspectorState from './JInspectorState';
import jInspectorHandler from './JInspectorHandler';

import './JInspector.css';

import { useInspector } from '../../../contexts/InspectorContext';
import { useUserSetting } from '../../../contexts/UserSettingsContext';
// ADDED — service dropdown options from quota cache
import quotaEngine from '../../../services/engines/QuotaEngine';
import { ENTRY_TYPES } from '../types/entryTypes';
import { IMPORTANCE_OPTIONS } from '../types/importanceOptions';
import userTypeEngine from '../../../pages/DashboardComponents/UserDefinedTypes/UserTypeEngine';

// JInspector — hover inspector for ContentPanel header.
// Stays open when dirty — cancel or save to close.
// When closeJInspectorOnLeave is false, inspector stays open regardless of dirty state.
// Props:
//   entry    — current entry object
//   onSave   — fn({ title, stage, importanceId, prioId, type, cat, stateId, service, format, remark, isPinned }) called on save
//   children — the trigger element
export default function JInspector({ entry, onSave, children }) {
    const triggerRef = useRef(null);
    const hideTimerRef = useRef(null);
    const dragOffset = useRef({ x: 0, y: 0 });

    const { setIsDirty } = useInspector();
    const autoOpen = useUserSetting('contentPanel.autoOpenJInspector');
    // ADDED — when false, mouse leave never closes the inspector; user must cancel or save
    const closeOnLeave = useUserSetting('contentPanel.closeJInspectorOnLeave');

    const [visible, setVisible] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const [dragging, setDragging] = useState(false);

    // Snapshot of entry at open time — single source of truth for defaults
    const snapshot = new JInspectorState(entry);

    // Form fields consolidated into a single object (was 10 separate useState's). One update
    // path via setForm, no setters-map. Keys match JInspectorState/entry naming 1:1 (type,
    // isPinned) — no remapping needed when building `current` below.
    const [form, setForm] = useState(() => ({
        title: snapshot.title,
        stage: snapshot.stage,
        importanceId: snapshot.importanceId,
        prioId: snapshot.prioId,
        type: snapshot.type,
        cat: snapshot.cat,
        stateId: snapshot.stateId,
        format: snapshot.format,
        remark: snapshot.remark,
        isPinned: snapshot.isPinned,
        service: snapshot.service,
        tid: snapshot.tid,
    }));

    // Current state — compared against snapshot for isDirty. Same shape as snapshot now
    // (both built from an object using type/isPinned) — no key remapping.
    const current = new JInspectorState(form);

    // isDirty delegated to JInspectorState — no inline comparisons in component
    const isDirty = snapshot.isDirty(current);

    // Applies entry values to form state — used on open, cancel and auto-open
    const applyEntry = (e) => {
        setForm({
            title: e?.title ?? '',
            stage: e?.stage ?? '',
            importanceId: e?.importanceId ?? 0,
            prioId: e?.prioId ?? 0,
            type: e?.type ?? '',
            cat: e?.cat ?? '',
            stateId: e?.stateId ?? 0,
            service: e?.service ?? '',
            format: e?.format ?? '',
            remark: e?.remark ?? '',
            isPinned: e?.isPinned ?? false,
            tid: e?.tid ?? '',
        });
    };

    // Generic field change handler — single update path, no setters-map needed anymore
    const handleChange = (field, value) => {
        setForm((f) => ({ ...f, [field]: value }));
    };

    const scheduleHide = () => {
        hideTimerRef.current = setTimeout(() => setVisible(false), 120);
    };

    const cancelHide = () => {
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };

    // CHANGED — only schedule hide when closeOnLeave is enabled; dirty always blocks close
    const handleMouseLeave = () => {
        if (!isDirty && closeOnLeave) scheduleHide();
    };

    const handleShow = (e) => {
        cancelHide();
        if (!triggerRef.current) return;
        jInspectorHandler.handleShow(e, entry, applyEntry, setPos, setVisible);
    };

    const handleCancel = () => {
        jInspectorHandler.handleCancel(entry, applyEntry, setIsDirty, setVisible);
    };

    const handleSave = () => {
        jInspectorHandler.handleSave(isDirty, current, onSave, setIsDirty, setVisible);
    };

    const handleDragStart = (e) => {
        dragOffset.current = { x: e.clientX - pos.left, y: e.clientY - pos.top };
        setDragging(true);
    };

    const handleDragMove = (e) => {
        if (!dragging) return;
        setPos({ left: e.clientX - dragOffset.current.x, top: e.clientY - dragOffset.current.y });
    };

    const handleDragEnd = () => {
        setDragging(false);
    };

    // Listener rebuilt on every state change — current is always fresh on Enter
    useEffect(() => {
        if (!visible) return;
        const onKey = (e) => jInspectorHandler.handleKey(e, isDirty, handleSave, handleCancel);
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [visible, isDirty, form]);

    // Sync dirty state to InspectorContext so parent can block navigation
    useEffect(() => {
        if (!visible) return;
        setIsDirty(isDirty);
    }, [isDirty, visible]);

    // Auto-open inspector when entry changes and setting is enabled
    useEffect(() => {
        if (!autoOpen || !triggerRef.current) return;
        const trigger = triggerRef.current.getBoundingClientRect();
        applyEntry(entry);
        setPos({ top: trigger.bottom + 4, left: trigger.left });
        setVisible(true);
    }, [autoOpen, entry?.rk]);

    const inspector = visible ? (
        <div
            className="j-inspector"
            style={{ top: pos.top, left: pos.left }}
            onMouseEnter={cancelHide}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
        >
            <JInspectorTitleBar
                rkPrefix={entry?.rk?.substring(0, 8)}
                isDirty={isDirty}
                dragging={dragging}
                onDragStart={handleDragStart}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <JInspectorFields
                current={current}
                importanceOptions={IMPORTANCE_OPTIONS}
                entryTypes={userTypeEngine.getDropdownItems()}
                serviceOptions={quotaEngine.getDropdownItems()}
                isDirty={isDirty}
                onChange={handleChange}
                onSave={handleSave}
                onCancel={handleCancel}
            />
            <JInspectorReadonly entry={entry} />
        </div>
    ) : null;

    return (
        <>
            <div
                ref={triggerRef}
                className="j-tooltip-trigger"
                onMouseEnter={autoOpen ? handleShow : undefined}
                onMouseLeave={handleMouseLeave}
                onClick={autoOpen ? undefined : handleShow}
            >
                {children}
            </div>
            {createPortal(inspector, document.body)}
        </>
    );
}
